import React, { useContext } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
} from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import { useMutation } from '@apollo/client';
import * as Yup from 'yup';
import get from 'lodash/get';

import Input from '../../../components/Input';
import UserContext from '../../../contexts/UserContext';
import { INTERNAL_TOGGLE_AP_ACTIVE_STATUS } from '../query';
import { useCustomToast } from '../../../hooks/useCustomToast';
import { useSpinner } from '../../../contexts/SpinnerContext';

const ToggleAccessPointActiveStatusSchema = Yup.object().shape({
  comment: Yup.string().required('Du musst einen Kommentar/Begründung eingeben'),
});

interface ToggleAccessPointActiveStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  refetch: () => void;
  accessPointID: string;
  active: boolean; // the target state: true = activate, false = deactivate/pause
}

const ToggleAccessPointActiveStatusModal = ({
  isOpen,
  onClose,
  refetch,
  accessPointID,
  active,
}: ToggleAccessPointActiveStatusModalProps) => {
  const [toggleActiveStatus] = useMutation(INTERNAL_TOGGLE_AP_ACTIVE_STATUS);
  const { user } = useContext(UserContext);
  const toast = useCustomToast();
  const { toggleSpinner } = useSpinner();

  const title = active ? 'Access Point aktivieren' : 'Access Point pausieren';
  const description = active
    ? 'Möchtest du den Access Point wirklich aktivieren? Die 14-Tage-Sperre wird zurückgesetzt.'
    : 'Möchtest du den Access Point wirklich pausieren? Die 14-Tage-Sperre wird zurückgesetzt.';

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>

        <Formik
          initialValues={{ comment: '' }}
          validationSchema={ToggleAccessPointActiveStatusSchema}
          onSubmit={async ({ comment }) => {
            try {
              toggleSpinner(true);
              onClose();
              await toggleActiveStatus({
                variables: {
                  accessPointID,
                  active,
                  internalUserId: user.id,
                  comment,
                },
              });
              await refetch();
              toast({
                title: 'Erfolg!',
                description: active
                  ? 'Der Access Point wurde erfolgreich aktiviert.'
                  : 'Der Access Point wurde erfolgreich pausiert.',
                status: 'success',
              });
            } catch (error) {
              console.log(error);
              toast({
                description: get(error, 'graphQLErrors.0.message', 'Ein Fehler ist aufgetreten!'),
                status: 'error',
              });
            } finally {
              toggleSpinner(false);
            }
          }}>
          {() => (
            <Form>
              <ModalBody>
                <Text fontSize="sm" mb="4">
                  {description}
                </Text>
                <Input name="comment" label="Kommentar/Begründung" />
              </ModalBody>

              <ModalFooter>
                <Button variant="ghost" size="sm" colorScheme="red" type="button" onClick={onClose}>
                  Abbrechen
                </Button>
                <Button ml="4" size="sm" colorScheme="brand" type="submit">
                  Bestätigen
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
        <ModalCloseButton />
      </ModalContent>
    </Modal>
  );
};

export default ToggleAccessPointActiveStatusModal;
